import type { AiProcessingResultsResponse } from "../types/listOfRecordingsTypes";
import { customInterceptor } from "../api/Interceptor";
import jwt_Decode from "jwt-decode";
import { getCoachDetails } from "../api/Patient";

type SuspectSegment = {
  t_start: number;
  t_end: number;
  reason?: {
    explanation?: string;
    agent_id?: string;
  };
};

/**
 * ✅ Fetch signed video URL
 * (Keep existing implementation until Azure signed URL API is finalized)
 */
async function getSignedVideoUrl(
  sessionId: string,
  objectName: string
): Promise<string> {
  try {
    const params = new URLSearchParams({
      objectName,
    });

    const result = await customInterceptor(
      `stream/${sessionId}/video?${params.toString()}`
    );

    if (!result?.url) {
      throw new Error(
        "Signed URL not found"
      );
    }

    return result.url;
  } catch (error) {
    console.error(
      "getSignedVideoUrl error:",
      error
    );

    throw error;
  }
}


/**
 * Get Pending Review Count
 */

export async function getPendingReviewCount(
  projectId: string
): Promise<number> {
  try {
    const result = await customInterceptor(
      `video-meta/ai-processing/pending-review-count?projectId=${projectId}`
    );

    if (!result?.success) {
      throw new Error(
        result?.message || "Failed to fetch pending review count"
      );
    }

    return result?.count ?? 0;
  } catch (error) {
    console.error("getPendingReviewCount error:", error);
    throw error;
  }
}

/**
 * Get list of videos
 */

export async function getVideos(
  projectId: string,
  limit = 10,
  offset = 0,
  status?: string
): Promise<AiProcessingResultsResponse> {
  try {

    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      projectId
    });

    if (status && status !== "all") {
      params.append("status", status);
    }

    const result: AiProcessingResultsResponse =
      await customInterceptor(
        `video-meta/ai-processing/results?${params.toString()}`
      );

    if (!result.success) {
      throw new Error(
        result.message || "Failed to fetch videos"
      );
    }

    return result;
  } catch (error) {
    console.error("getVideos error:", error);
    throw error;
  }
}

/**
 * Get video details
 */
export async function getVideoById(videoId: string) {
  try {
    const result = await customInterceptor(
      `video-meta/ai-processing/get-result/${videoId}`
    );

    if (!result.success) {
      throw new Error(
        result.message || "Failed to fetch video"
      );
    }

    const api = result.data;

    /**
     * Azure AI payload
     */
    const outcome = api.Data || {};
    const suspectSegments: SuspectSegment[] =
      outcome.suspect_segments || [];

    /**
     * Getting signed URL
     */
    const objectName = api.VideoPath;
    const sessionId = api.SessionId;

    const videoSrc =
      await getSignedVideoUrl(
        sessionId,
        objectName
      );

    const mapped: import("../types/videoTypes").VideoRecord =
    {
      videoId: api.SessionId,

      participantId:
        api.UserId || "",

      title: `Session ${api.SessionId.slice(
        0,
        8
      )}`,

      reviewStatus:
        api.Status?.toLowerCase() ===
        "completed"
          ? "completed"
          : "pending",
      videoPath: api.VideoPath,
      videoSrc,
      aiProcessingId: api.Id,
      durationSec: outcome.video_duration_sec || 0,
      framesAnalyzed: outcome.total_frames || 0,
      sampledFps: Number(outcome.fps? outcome.fps.toFixed(2): 0),
      videoQuality: { lightLevel: outcome.light_intensity_summary ?.final_label || "Unknown",

        lowLightPct:
          outcome.light_intensity_summary
            ?.bad_percent || 0,

        blurFrames:
          outcome.frame_quality_summary
            ?.num_blur_frames || 0,

        blackoutFrames:
          outcome.frame_quality_summary
            ?.num_blackout_frames || 0,

        final_decision:
          outcome.final_decision,
      },

      behavioralEvents:
        suspectSegments.map((seg) => ({
          tSec: seg.t_start,
          startSec: seg.t_start,
          endSec: seg.t_end,

          event:
            seg.reason?.explanation ||
            "Unknown Event",

          severity: "warn",

          barsImpact:
            seg.reason?.explanation || "",
        })),

      deceptionSignals: {
        multiplePersonFrames:
          outcome.person_detection_summary?.[
            "Multiple person"
          ]
            ? "Yes"
            : "No",

        zeroPersonFrames:
          outcome.person_detection_summary?.[
            "No person"
          ]
            ? "Yes"
            : "No",

        samePersonFrames:
          outcome.person_detection_summary?.[
            "same_person"
          ]
            ? "Yes"
            : "No",

        deceptionConfidence:
          outcome.overall_confidence || 0,
      },

      aiAssessment: {
        preparationScore:
          outcome.preparation_score || 0,

        executionScore:
          outcome.execution_score || 0,

        completionScore:
          outcome.completion_score || 0,

        overallScore:
          outcome.overall_score || 0,

        deceptionFlag:
          outcome.final_decision,

        rationale:
          outcome.summary_report || "",
      },
    };

    return mapped;
  } catch (error) {
    console.error(
      "getVideoById error:",
      error
    );
    throw error;
  }
}

/**
 * ✅ Submit review (mock)
 */
export async function submitVideoReview(payload: {
  aiProcessingId: string;
  reviewerId: string;
  preparation: number;
  execution: number;
  completion: number;
  overAll: number;
  feedback: string;
  status: "ACCEPTED" | "REJECTED";
}) {
  try {
    const coach = JSON.parse(sessionStorage.getItem("coach") || "{}");
    const result = await customInterceptor("video-meta/ai-processing/add-review",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          aiProcessingId: payload.aiProcessingId,
          reviewerId : coach?.Id,
          preparation: String(payload.preparation),
          execution: String(payload.execution),
          completion: String(payload.completion),
          overAll: String(payload.overAll),
          feedback: payload.feedback,
          status: payload.status,
        }),
      }
    );

    if (!result?.success) {
      throw new Error(
        result?.message ||
          "Failed to submit review"
      );
    }

    return result;
  } catch (error) {
    console.error(
      "submitVideoReview error:",
      error
    );

    throw error;
  }
}


